import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";

import { S3, WIDGET_ICONS } from "../../../../../../constants";
import useCompareState from "../../../../../../hooks/useCompareState";
import FileUpload from "../../../../../../parts/inputs/file-upload";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import ImageWidgetUploadInput from "../../../../../../parts/inputs/image-widget-upload";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import SvgIcon from "../../../../../../parts/SvgIcon";
import { handleGenericRefresh } from "../../../../../../utils/app-helpers";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { EditFile } from "./EditFile";
import WidgetManagerHeader from "./WidgetManagerHeader";

const publishWidget = (e, id) => {
  e.stopPropagation();
  e.preventDefault();
  submit(`/actions/meal-plan-library/status/${id}`, {
    method: "POST",
    body: { status: "published" },
  });
};

const unpublishWidget = (e, id, file) => {
  e.stopPropagation();
  e.preventDefault();
  const fileId = file?.file;
  submit(`/actions/meal-plan-library/status/${id}`, {
    method: "POST",
    body: { status: "draft", fileId },
  });
};

const WidgetToggle = ({ status, id, file }) => {
  const { t } = useTranslation("coaching-portal");
  const [value, setValue] = useState(status !== "draft");
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);

  const handleChange = (e) => {
    !value ? unpublishWidget(e, id, file) : publishWidget(e, id);
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 500);
  };
  return (
    <>
      <input
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onChange={() => {
          setValue(!value);
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        checked={value ? true : undefined}
      />
      <ConfirmationModal
        title={!value ? t("widgets.common.unpublish") : t("widgets.common.publish")}
        contentText={!value ? t("widgets.mealPlanLibraryManager.confirmUnpublishMealPlan") : t("widgets.mealPlanLibraryManager.confirmPublishMealPlan")}
        cancelButtonText={t("widgets.common.cancel")}
        cancelButtonAction={() => {
          setOpenedConfirmationModal(false);
          setValue(!value);
        }}
        confirmButtonText={!value ? t("widgets.common.unpublish") : t("widgets.common.publish")}
        confirmButtonAction={(e) => handleChange(e)}
        isOpen={openedConfirmationModal}
        confirmSuccess={value}
      />
    </>
  );
};

export const MealPlanLibraryWidgetManager = ({ locals, goBack }) => {
  const { t } = useTranslation("coaching-portal");
  const { activeUser, activeWidget, organizationOwner, mealPlanLibraries } =
    locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);
  const [formState, setFormState] = useState({});
  const [editState, setEditState] = useState({
    //state to edit a file
    name: "",
    file: "",
    description: "",
    widget: "",
    _id: "",
  });
  const [addNew, setAddNew] = useState("addNewButton");
  const [statusState, setStatusState] = useState("initial");
  const [mealPlanLibrariesState, setMealPlanLibrariesState] = useState([]);
  const [editFileState, setEditFileState] = useState("");
  const [fileFormState, setFileFormState] = useState({
    file: "",
    name: "",
    description: "",
    image: "",
  });
  const [imageState, setImageState] = useState(null);
  const [fileState, setFileState] = useState(null);
  const [refreshList, setRefreshList] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);
  const [changedOrder, setChangedOrder] = useState(false);

  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const fileInput = element.querySelector(".file-section-div");
                  const imageInput =
                    element.querySelector(".image-section-div");
                  const nameInput = element.querySelector(".name-section-div");
                  const descriptionInput = element.querySelector(
                    ".description-section-div"
                  );
                  const idInput = element.querySelector(".id-section-div");
                  const statusInput = element.querySelector(
                    ".status-section-div"
                  );
                  return {
                    file: (fileInput && JSON.parse(fileInput.value)) || "",
                    status: (statusInput && statusInput.value) || "",
                    description:
                      (descriptionInput && descriptionInput.textContent) || "",
                    name: (nameInput && nameInput.textContent) || "",
                    widget: activeWidget._id,
                    _id: (idInput && idInput.value) || "",
                    image: (imageInput && JSON.parse(imageInput.value)) || "",
                  };
                });
                setMealPlanLibrariesState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  /**
   * Function to reset the state of the component
   */
  const resetState = () => {
    setFileFormState({
      file: "",
      name: "",
      description: "",
      image: "",
    });
    setFileState(null);
    setImageState(null);
    setAddNew("addNewButton");
    setEditFileState("");
    setStatusState("initial");
  };

  useEffect(() => {
    if (locals.query.submited) {
      resetState();
    }
  }, [locals.query]);

  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (activeWidget) {
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setFormState({
        ...comparingBase,
        id: activeWidget._id,
      });
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (mealPlanLibraries) {
      setMealPlanLibrariesState(mealPlanLibraries);
    }
  }, [activeWidget, mealPlanLibraries]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  const validationSubmit = () => {
    return !!(description.length > 0 && name.length > 0 && icon.length > 0);
  };

  const handleDelete = (e, id) => {
    if (
      process.browser &&
      confirm(t("widgets.mealPlanLibraryManager.confirmDeleteMealPlan"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/meal-plan-library/delete/${id}`, {
        method: "GET",
      });
    }
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = { ...formState, [targetName]: target.value };
    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    setEditFileState("");
    setStatusState("initial");
    submit(`/actions/meal-plan-library/save/${editState._id}`, {
      method: "POST",
      body: editState,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };

  const handleChangeAddNewState = () => {
    setAddNew("addNewForm");
  };

  const handleChangeEditState = (state) => {
    setStatusState(state);
  };

  const deleteWidget = (e) => {
    if (activeWidget) {
      const { _id } = activeWidget;
      deleteWidgetUtil(e, _id);
    }
  };

  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedMealPlans = mealPlanLibrariesState.map(
      (element) => element._id
    );

    if (orderedMealPlans.length > 0) {
      setStatusState("initial");
      submit("/actions/meal-plan-library/reorder", {
        method: "POST",
        body: { orderedMealPlans },
      });
    }
  };
  const cancelReorder = () => {
    setStatusState("initial");
    setMealPlanLibrariesState(mealPlanLibraries);
    setRefreshList(!refreshList);
  };

  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  const validationFileSubmit = () => {
    return !!(
      fileFormState.name.length > 0 &&
      fileFormState.description.length > 0 &&
      imageState !== null &&
      fileState !== null
    );
  };

  const handleInputFileChange = ({ target }) => {
    const newState = { ...fileFormState, [target.name]: target.value };
    setFileFormState(newState);
  };

  const handleImageUpload = (newImage) => {
    setImageState(newImage);
  };

  const handleFileUpload = (newFile) => {
    setFileState(newFile);
  };

  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  /**
   * Handle edit widget if it's PDF or template
   * @param {*} name
   * @param {*} widget
   * @param {*} _id
   * @param {*} description
   * @param {*} file
   * @param {*} mealPlan
   */
  const handleEdit = (name, widget, _id, description, file, mealPlan) => {
    if (file && !mealPlan) {
      setEditState({
        name,
        widget,
        _id,
        description,
        file,
      });
      handleChangeEditState("edit");
      setEditFileState(_id);
      handleGenericRefresh({ submited: "" });
    } else if (!file && mealPlan) {
      handleGenericRefresh({
        "modal[type]": "in-app-meal-planner",
        submited: "",
        selectedMealPlanLibraryId: _id,
      });
    }
  };

  const WidgetTogglePush = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.subscriptionsPush")}</span>
        <input
          type="checkbox"
          id="switch-1"
          name="switch-1"
          role="switch"
          onChange={() => {
            const newState = {
              ...formState,
              pushPermission: !formState.pushPermission,
            };
            setFormState(newState);
            setCurrentCompareState({
              ...compareBase,
              pushPermission: !formState.pushPermission,
            });
          }}
          checked={permission ? true : undefined}
        />
    </div>
  );

  const WidgetTogglePull = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.coachingPull")}</span>
        <input
          type="checkbox"
          id="switch-1"
          name="switch-1"
          role="switch"
          onChange={() => {
            const newState = {
              ...formState,
              pullPermission: !formState.pullPermission,
            };
            setFormState(newState);
            setCurrentCompareState({
              ...compareBase,
              pullPermission: !formState.pullPermission,
            });
          }}
          checked={permission ? true : undefined}
        />
    </div>
  );

  const DashedBox = ({ label, onClick }) => {
    return (
      <div className="dashed-box" onClick={onClick}>
        <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />
        <span>{label}</span>
      </div>
    );
  };

  return (
    <main className="widgets-manager list files-docs">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["meal-plan-library"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label htmlFor="name">{t("widgets.common.widgetName")}</label>
              <input
                maxLength="30"
                name="name"
                className="text-input"
                placeholder={`Enter Name`}
                value={name}
                onChange={handleInputChange}
              />
              <label htmlFor="description">{t("widgets.common.widgetDescription")}</label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={`Enter description here..`}
                value={description}
                onChange={handleInputChange}
              />
              <ImageRadio
                label={t("widgets.common.widgetIcon")}
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["meal-plan-library"]}
                onChange={handleInputChange}
                isManager={true}
              />
              <label>{t("widgets.common.userTypeAccess")}</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {userToRequest.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
              >
                {t("widgets.common.delete")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
              >
                {t("widgets.common.save")}
              </button>
            </footer>
          </aside>
        )}
        <section>
          <header>
            <section className="only-show-mobile back-handler">
              <SvgIcon
                src={"/img/new-icons/back-button.svg"}
                onClickFn={() =>
                  submit(`/actions/refresh/?`, {
                    method: "GET",
                    body: { section: "", learn: "" },
                  })
                }
              />
              <h3>{activeWidget?.name || t("widgets.common.explore")}</h3>
            </section>
            <hgroup className="mobile-hidden">
              <h4>{t("widgets.mealPlanLibraryManager.mealPlanLibrary")}</h4>
              <p className="p6">{t("widgets.mealPlanLibraryManager.addNewMealPlan")}</p>
            </hgroup>
            {statusState === "initial" ? (
              <button
                data-button-type="secondary"
                onClick={() => handleChangeStatusState("change-order")}
              >
                {t("widgets.common.changeOrder")}
              </button>
            ) : (
              <hgroup className="actions-wrapper">
                <button
                  data-button-type="primary"
                  onClick={
                    statusState === "change-order"
                      ? handleSaveReorder
                      : handleEditSubmit
                  }
                  disabled={!changedOrder}
                >
                  {t("widgets.common.save")}
                </button>
                {statusState === "change-order" && (
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={cancelReorder}
                  >
                    {t("widgets.common.cancel")}
                  </button>
                )}
              </hgroup>
            )}
          </header>
          <div className="principal-grabber-div">
            {!!mealPlanLibraries &&
              orderWidgets(mealPlanLibrariesState).map(
                ({
                  _id,
                  file,
                  name,
                  description,
                  widget,
                  status,
                  image,
                  mealPlan,
                }) => {
                  return editFileState !== _id ||
                    statusState === "change-order" ? (
                    <div
                      key={`${_id}-${refreshList}`}
                      className="article-wrapper files-docs"
                    >
                      <article
                        id={_id}
                        className={`item-grabber-container${
                          statusState === "change-order" ? " column" : ""
                        }`}
                      >
                        <div className="column width-widget-information">
                          <div>
                            <input
                              className="file-section-div"
                              name="file"
                              style={{ display: "none" }}
                              readOnly
                              value={JSON.stringify(file)}
                            />
                            <input
                              className="image-section-div"
                              name="image"
                              style={{ display: "none" }}
                              readOnly
                              value={JSON.stringify(image)}
                            />
                            <input
                              className="id-section-div"
                              name="id"
                              style={{ display: "none" }}
                              readOnly
                              value={_id}
                            />
                            <input
                              className="status-section-div"
                              name="status"
                              style={{ display: "none" }}
                              readOnly
                              value={status}
                            />
                            <img src={S3.toCDN(image?.s3Path)} loading="lazy" />
                          </div>
                          <div>
                            <p className="s2">{name}</p>
                            <p className="p4 opacity-70">{description}</p>
                          </div>
                        </div>
                        <div className="action-buttons">
                          <div>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src="/img/new-icons/edit-pencil.svg"
                                onClickFn={() => {
                                  handleEdit(
                                    name,
                                    widget,
                                    _id,
                                    description,
                                    file,
                                    mealPlan
                                  );
                                }}
                              />
                            </button>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src={"/img/new-icons/red-trash.svg"}
                                onClickFn={(event) => handleDelete(event, _id)}
                                noFilterOnLight={true}
                              />
                            </button>
                          </div>
                          <div>
                            <WidgetToggle
                              status={status}
                              id={_id}
                              file={file}
                            />
                          </div>
                        </div>
                      </article>
                      {statusState === "change-order" && (
                        <div className="drag-wrapper">
                          <SvgIcon
                            src="/img/new-icons/menu.svg"
                            mobileSrc="/img/new-icons/drag.svg"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <EditFile
                      nameProps={name}
                      widgetProps={widget}
                      _idProps={_id}
                      descriptionProps={description}
                      fileProps={file}
                      imageProps={image}
                      widgetType={locals.query.type}
                    />
                  );
                }
              )}
          </div>
          {addNew === "addNewButton" ? (
            <div className="wrapper">
              <DashedBox
                label={t("widgets.mealPlanLibraryManager.addViaInApp")}
                onClick={() => {
                  handleGenericRefresh({
                    "modal[type]": "in-app-meal-planner",
                    submited: "",
                  });
                }}
              />
              <DashedBox
                label={t("widgets.mealPlanLibraryManager.addViaPdf")}
                onClick={() => {
                  handleChangeAddNewState();
                  handleGenericRefresh({
                    submited: "",
                  });
                }}
              />
            </div>
          ) : (
            <form
              encType="multipart/form-data"
              method="POST"
              data-noreset
              action={`/actions/meal-plan-library/save/`}
              className="meal-plan-library-pdf-form"
            >
              <div className="title">
                <div className="text">
                  <SvgIcon
                    src="/img/new-icons/arrow-left.svg"
                    onClickFn={resetState}
                    classNames="cursor-pointer"
                  />
                  <h4>{t("widgets.mealPlanLibraryManager.addViaPdfHeading")}</h4>
                </div>
                <button
                  className="close-button"
                  type="button"
                  onClick={resetState}
                >
                  <SvgIcon
                    src="/img/new-icons/red-cross.svg"
                    classNames="cursor-pointer"
                    noFilterOnLight={true}
                  />
                </button>
              </div>
              <div className="content">
                <div>
                  <FileUpload
                    name="file"
                    prefix=""
                    handleFileUpload={handleFileUpload}
                  />

                  <ImageWidgetUploadInput
                    name="image"
                    prefix=""
                    defaultValue={fileFormState.image}
                    handleImageUpload={handleImageUpload}
                    showIcon={false}
                  />
                </div>
                <div>
                  <div>
                    <div className="input-box">
                      <label htmlFor="name">
                        {t("widgets.mealPlanLibraryManager.fileNameLabel")}
                      </label>
                      <input
                        name="name"
                        className="input-videoplaylist-name text-input"
                        placeholder={t(
                          "widgets.mealPlanLibraryManager.fileNamePlaceholder",
                        )}
                        value={fileFormState.name}
                        onChange={handleInputFileChange}
                      />
                    </div>
                    <div className="input-box">
                      <label htmlFor="description">
                        {t("widgets.mealPlanLibraryManager.descriptionLabel")}
                      </label>
                      <input
                        name="description"
                        className="input-videoplaylist-name text-input"
                        value={fileFormState.description}
                        onChange={handleInputFileChange}
                        placeholder={t("widgets.common.enterDescription")}
                      />
                    </div>
                  </div>
                  <input
                    name="widget"
                    style={{ display: "none" }}
                    defaultValue={id}
                  />
                  <button
                    disabled={!validationFileSubmit()}
                    data-button-type="primary"
                  >
                    {t("widgets.common.save")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};
